import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Loader2, Mail, User } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { AuthAlert, AuthField, PasswordField } from '@/components/auth/AuthFormControls';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useSiteName } from '@/hooks/useSiteName';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  createLoginSchema,
  createRegisterSchema,
  type LoginFormData,
  type RegisterFormData,
} from '@/utils/validation';

type OAuthProvider = 'google' | 'facebook';

type AuthFormProps = {
  redirectTo: string;
  onSuccess: () => void;
  onSwitchMode?: () => void;
  onNavigateAway?: () => void;
  idPrefix?: string;
  headingLevel?: 'h1' | 'h2';
};

const switchClassName =
  'font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function LoginForm({
  redirectTo,
  onSuccess,
  onSwitchMode,
  onNavigateAway,
  idPrefix = '',
  headingLevel = 'h1',
}: AuthFormProps) {
  const { t } = useI18n();
  const siteName = useSiteName();
  const { login, loginWithGoogle, loginWithFacebook } = useAuth();
  const [serverError, setServerError] = useState('');
  const [oauthProvider, setOAuthProvider] = useState<OAuthProvider | null>(null);
  const oauthStartingRef = useRef(false);
  const loginFormSchema = useMemo(() => createLoginSchema(), []);
  const Heading = headingLevel;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setServerError('');
      await login({
        email: data.email.trim().toLowerCase(),
        password: data.password,
      });
      onSuccess();
    } catch (error: unknown) {
      setServerError(getApiErrorMessage(error, t('auth.loginFailed')));
    }
  };

  const startOAuth = async (provider: OAuthProvider) => {
    if (oauthStartingRef.current) return;

    oauthStartingRef.current = true;
    setServerError('');
    setOAuthProvider(provider);

    try {
      // The backend stores the post-login path alongside the OAuth state.
      const started =
        provider === 'google'
          ? await loginWithGoogle(redirectTo)
          : await loginWithFacebook(redirectTo);

      if (!started) {
        setServerError(t('auth.oauthStartFailed'));
      }
    } catch (error: unknown) {
      setServerError(getApiErrorMessage(error, t('auth.oauthStartFailed')));
    } finally {
      oauthStartingRef.current = false;
      setOAuthProvider(null);
    }
  };

  const isBusy = isSubmitting || oauthProvider !== null;

  return (
    <div className="mx-auto w-full max-w-md space-y-6">
      <header className="space-y-2 text-center sm:text-left">
        <p className="text-sm font-semibold text-primary">
          {t('auth.welcomeBack')}
        </p>
        <Heading className="text-2xl font-bold tracking-tight sm:text-3xl">
          {t('auth.login')}
        </Heading>
        <p className="text-sm leading-6 text-muted-foreground">
          {t('auth.loginSubtitle')}
        </p>
      </header>

      <AuthAlert message={serverError} />

      <form
        className="space-y-4"
        noValidate
        onChange={() => serverError && setServerError('')}
        onSubmit={handleSubmit(onSubmit)}
      >
        <AuthField
          id={`${idPrefix}email`}
          type="email"
          label={t('auth.email')}
          icon={Mail}
          placeholder={t('auth.emailPlaceholder')}
          autoComplete="email"
          inputMode="email"
          error={errors.email?.message ? t(errors.email.message) : undefined}
          {...register('email')}
        />

        <div>
          <div className="mb-1.5 flex items-center justify-end">
            <Link
              to="/password-reset"
              onClick={onNavigateAway}
              className="rounded-sm text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
          <PasswordField
            id={`${idPrefix}password`}
            label={t('auth.password')}
            placeholder={t('auth.passwordPlaceholder')}
            autoComplete="current-password"
            error={
              errors.password?.message ? t(errors.password.message) : undefined
            }
            showPasswordLabel={t('auth.showPassword')}
            hidePasswordLabel={t('auth.hidePassword')}
            capsLockMessage={t('auth.capsLockOn')}
            {...register('password')}
          />
        </div>

        <Button
          type="submit"
          className="h-12 w-full rounded-xl text-base font-semibold"
          disabled={isBusy}
          aria-busy={isSubmitting}
        >
          {isSubmitting && (
            <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
          )}
          {isSubmitting ? t('auth.loggingIn') : t('auth.loginButton')}
        </Button>
      </form>

      <div className="relative" aria-hidden="true">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-3 text-muted-foreground">
            {t('auth.orContinueWith')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Button
          variant="outline"
          type="button"
          className="h-12 rounded-xl whitespace-normal"
          disabled={isBusy}
          aria-busy={oauthProvider === 'google'}
          onClick={() => void startOAuth('google')}
        >
          {oauthProvider === 'google' ? (
            <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <svg aria-hidden="true" className="mr-2 h-4 w-4 shrink-0" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
          )}
          {t('auth.login_with_google')}
        </Button>
        <Button
          variant="outline"
          type="button"
          className="h-12 rounded-xl whitespace-normal"
          disabled={isBusy}
          aria-busy={oauthProvider === 'facebook'}
          onClick={() => void startOAuth('facebook')}
        >
          {oauthProvider === 'facebook' ? (
            <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <svg aria-hidden="true" className="mr-2 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          )}
          {t('auth.login_with_facebook')}
        </Button>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        {t('auth.noAccount', { siteName })}{' '}
        {onSwitchMode ? (
          <button type="button" className={switchClassName} onClick={onSwitchMode}>
            {t('auth.registerLink')}
          </button>
        ) : (
          <Link to="/register" state={{ from: redirectTo }} className={switchClassName}>
            {t('auth.registerLink')}
          </Link>
        )}
      </p>
    </div>
  );
}

export function RegisterForm({
  redirectTo,
  onSuccess,
  onSwitchMode,
  onNavigateAway,
  idPrefix = '',
  headingLevel = 'h1',
}: AuthFormProps) {
  const { t } = useI18n();
  const { register: registerUser } = useAuth();
  const [serverError, setServerError] = useState('');
  const registerFormSchema = useMemo(() => createRegisterSchema(), []);
  const Heading = headingLevel;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    mode: 'onChange',
  });

  const password = useWatch({ control, name: 'password', defaultValue: '' });
  const meetsPasswordLength = password.length >= 8;

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setServerError('');
      await registerUser({
        email: data.email.trim().toLowerCase(),
        password: data.password,
        nickname: data.nickname.trim(),
      });
      onSuccess();
    } catch (error: unknown) {
      setServerError(getApiErrorMessage(error, t('auth.registerFailed')));
    }
  };

  return (
    <div className="mx-auto w-full max-w-md space-y-6">
      <header className="space-y-2 text-center sm:text-left">
        <p className="text-sm font-semibold text-primary">
          {t('auth.startJourney')}
        </p>
        <Heading className="text-2xl font-bold tracking-tight sm:text-3xl">
          {t('auth.register')}
        </Heading>
        <p className="text-sm leading-6 text-muted-foreground">
          {t('auth.registerSubtitle')}
        </p>
      </header>

      <AuthAlert message={serverError} />

      <form
        className="space-y-4"
        noValidate
        onChange={() => serverError && setServerError('')}
        onSubmit={handleSubmit(onSubmit)}
      >
        <AuthField
          id={`${idPrefix}nickname`}
          type="text"
          label={t('auth.nickname')}
          icon={User}
          placeholder={t('auth.nicknamePlaceholder')}
          autoComplete="nickname"
          error={errors.nickname?.message ? t(errors.nickname.message) : undefined}
          {...register('nickname')}
        />

        <AuthField
          id={`${idPrefix}email`}
          type="email"
          label={t('auth.email')}
          icon={Mail}
          placeholder={t('auth.emailPlaceholder')}
          autoComplete="email"
          inputMode="email"
          error={errors.email?.message ? t(errors.email.message) : undefined}
          {...register('email')}
        />

        <div>
          <PasswordField
            id={`${idPrefix}password`}
            label={t('auth.password')}
            placeholder={t('auth.passwordPlaceholder')}
            autoComplete="new-password"
            error={
              errors.password?.message ? t(errors.password.message) : undefined
            }
            showPasswordLabel={t('auth.showPassword')}
            hidePasswordLabel={t('auth.hidePassword')}
            capsLockMessage={t('auth.capsLockOn')}
            {...register('password')}
          />
          {!errors.password && (
            <p
              className={cn(
                'mt-2 flex items-center gap-1.5 text-xs',
                meetsPasswordLength
                  ? 'font-medium text-emerald-600'
                  : 'text-muted-foreground',
              )}
              aria-live="polite"
            >
              <Check aria-hidden="true" className="h-3.5 w-3.5" />
              {t('auth.passwordMinLength')}
            </p>
          )}
        </div>

        <PasswordField
          id={`${idPrefix}confirmPassword`}
          label={t('auth.confirmPassword')}
          placeholder={t('auth.confirmPasswordPlaceholder')}
          autoComplete="new-password"
          error={
            errors.confirmPassword?.message
              ? t(errors.confirmPassword.message)
              : undefined
          }
          showPasswordLabel={t('auth.showPassword')}
          hidePasswordLabel={t('auth.hidePassword')}
          capsLockMessage={t('auth.capsLockOn')}
          {...register('confirmPassword')}
        />

        <Button
          type="submit"
          className="h-12 w-full rounded-xl text-base font-semibold"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting && (
            <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
          )}
          {isSubmitting ? t('auth.registering') : t('auth.registerButton')}
        </Button>

        <p className="text-center text-xs leading-5 text-muted-foreground">
          {t('auth.termsPrefix')}{' '}
          <Link className="text-foreground underline underline-offset-2" to="/terms" onClick={onNavigateAway}>
            {t('auth.termsLink')}
          </Link>{' '}
          {t('auth.and')}{' '}
          <Link className="text-foreground underline underline-offset-2" to="/privacy" onClick={onNavigateAway}>
            {t('auth.privacyLink')}
          </Link>
        </p>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        {t('auth.hasAccount')}{' '}
        {onSwitchMode ? (
          <button type="button" className={switchClassName} onClick={onSwitchMode}>
            {t('auth.loginLink')}
          </button>
        ) : (
          <Link to="/login" state={{ from: redirectTo }} className={switchClassName}>
            {t('auth.loginLink')}
          </Link>
        )}
      </p>
    </div>
  );
}
